/**
 * Epic 278 Slice D — thin client mirror of
 * `cloud_api/.../safety_control_workspace_state.py`.
 *
 * Prefer server fields (`customer_*` on SafetyDashboardRow) when present.
 * Use this helper only for offline / synthetic history rows.
 */

export const CONTROL_STAGES = [
  'Confirmed',
  'Control under review',
  'Action assigned',
  'Implementation in progress',
  'Implementation verification required',
  'Follow-up measurement scheduled',
  'Measurement in progress',
  'Effectiveness review required',
  'Monitoring',
  'Closed',
  'Reopened',
] as const;

export type ControlStage = (typeof CONTROL_STAGES)[number];

export type ScanWorkflowState =
  | 'Scheduled'
  | 'Queued'
  | 'Running'
  | 'Completed'
  | 'Review required'
  | 'Awaiting sign-off'
  | 'Signed'
  | 'Insufficient coverage'
  | 'Failed'
  | 'Cancelled';

export type RecordState = 'Draft' | 'Awaiting sign-off' | 'Signed' | 'Superseded' | 'Withdrawn';

export type ResolvedWorkspaceState = {
  scanWorkflowState: ScanWorkflowState | null;
  scanResult: string | null;
  recordState: RecordState | null;
  controlStage: ControlStage | null;
  riskPatternWorkState: string | null;
  attentionWorkState: string | null;
  customerLabel: string;
  integrityWarnings: string[];
};

export type WorkspaceStateInput = {
  reportStatus?: string | null;
  signoffStatus?: string | null;
  historyStatus?: string | null;
  scheduledRunStatus?: string | null;
  impactMeasurementStatus?: string | null;
  recommendedNextStep?: string | null;
  blockerCodes?: string[];
  workflowStatus?: string | null;
  workflowStatusLabel?: string | null;
  correctiveActionStatus?: string | null;
  correctiveActionStatusLabel?: string | null;
  safetyCaseStatus?: string | null;
  safetyCaseStatusLabel?: string | null;
  isCandidate?: boolean;
  hasNewActivity?: boolean;
  isLatestSigned?: boolean | null;
  isWithdrawn?: boolean;
  caseExists?: boolean;
  compoundLabel?: string | null;
  /** Server-resolved fields — preferred when present. */
  customerScanWorkflowState?: string | null;
  customerRecordState?: string | null;
  customerControlStage?: string | null;
  customerRiskPatternWorkState?: string | null;
  customerAttentionWorkState?: string | null;
  customerStatusLabel?: string | null;
  integrityWarnings?: string[] | null;
};

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function collapseCompoundCustomerLabel(raw: unknown): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  const key = text.toLowerCase().replace(/\s+/g, ' ');
  const compact = key.replace(/\s/g, '');
  if (compact === 'signed·signed' || key === 'signed · signed') return 'Signed';
  if (
    compact === 'awaitingsign-off·unsigned'
    || compact === 'awaitingsignoff·unsigned'
    || key === 'awaiting sign-off · unsigned'
  ) {
    return 'Awaiting sign-off';
  }
  if (key.includes('assessment required')) return 'Review required';
  if (text.includes('·')) {
    const [left, right] = text.split('·').map((part) => part.trim());
    const leftN = normalize(left);
    const rightN = normalize(right);
    if (leftN && leftN === rightN) return left;
    if (['signed', 'unsigned', 'finalized', 'draft'].includes(rightN)) return left || right;
  }
  return text;
}

function hasCoverageIssue(input: WorkspaceStateInput): boolean {
  // Hard coverage only — soft/stale impact_measurement_pending must not collapse
  // report-ready drafts into Insufficient coverage (Attention + Scan badges).
  const step = normalize(input.recommendedNextStep);
  if (step === 'complete_impact_coverage' || step === 'resolve_coverage_issue') return true;
  const impact = normalize(input.impactMeasurementStatus);
  if (impact.includes('insufficient') || (impact.includes('coverage') && impact !== 'pending')) {
    return true;
  }
  return (input.blockerCodes || []).some((code) => {
    const normalized = normalize(code);
    return (
      normalized === 'complete_impact_coverage'
      || normalized === 'insufficient_coverage'
      || normalized === 'resolve_coverage_issue'
      || (normalized.includes('coverage') && !normalized.includes('measurement_pending'))
    );
  });
}

export function resolveScanWorkflowState(input: WorkspaceStateInput): {
  state: ScanWorkflowState | null;
  integrityWarnings: string[];
} {
  if (input.customerScanWorkflowState) {
    return { state: input.customerScanWorkflowState as ScanWorkflowState, integrityWarnings: [] };
  }

  const warnings: string[] = [];
  const report = normalize(input.historyStatus || input.reportStatus);
  const signoff = normalize(input.signoffStatus);
  const runStatus = normalize(input.scheduledRunStatus);

  const signedSignal = report === 'finalized' || report === 'signed' || signoff === 'completed';
  const awaitingRaw = (
    report === 'blocked'
    || report === 'draft'
    || report === 'awaiting_signoff'
    || report === 'awaiting_sign-off'
    || signoff === 'pending'
  );
  const cleanSigned = (report === 'finalized' || report === 'signed') && signoff === 'completed';

  if (signedSignal && awaitingRaw && !cleanSigned) {
    warnings.push('integrity: signed and awaiting-sign-off signals both present; customer badge collapsed to Signed');
    return { state: 'Signed', integrityWarnings: warnings };
  }
  if (signedSignal) return { state: 'Signed', integrityWarnings: warnings };
  if (report === 'candidate') return { state: null, integrityWarnings: warnings };

  const runMap: Record<string, ScanWorkflowState> = {
    expected: 'Scheduled',
    scheduled: 'Scheduled',
    queued: 'Queued',
    running: 'Running',
    failed: 'Failed',
    deferred: 'Cancelled',
    skipped_overlap: 'Cancelled',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
  };
  if (!report && runStatus && runMap[runStatus]) {
    return { state: runMap[runStatus], integrityWarnings: warnings };
  }

  if (report === 'failed' || (report.includes('fail') && report) || runStatus === 'failed') {
    return { state: 'Failed', integrityWarnings: warnings };
  }
  if (hasCoverageIssue(input) && (report === 'blocked' || report === 'draft' || !report)) {
    return { state: 'Insufficient coverage', integrityWarnings: warnings };
  }
  if (
    report === 'blocked'
    || signoff === 'pending'
    || report === 'draft'
    || report === 'awaiting_signoff'
    || report === 'awaiting_sign-off'
  ) {
    return { state: 'Awaiting sign-off', integrityWarnings: warnings };
  }
  if (report === 'completed') return { state: 'Completed', integrityWarnings: warnings };
  if (!report && !signoff && !runStatus) return { state: null, integrityWarnings: warnings };
  return { state: 'Review required', integrityWarnings: warnings };
}

export function resolveRecordState(input: WorkspaceStateInput): {
  state: RecordState | null;
  integrityWarnings: string[];
} {
  if (input.customerRecordState) {
    return { state: input.customerRecordState as RecordState, integrityWarnings: [] };
  }
  const warnings: string[] = [];
  const report = normalize(input.reportStatus);
  const signoff = normalize(input.signoffStatus);
  if (input.isWithdrawn) return { state: 'Withdrawn', integrityWarnings: warnings };
  const signed = report === 'finalized' || report === 'signed' || signoff === 'completed';
  if (signed) {
    if (input.isLatestSigned === false) return { state: 'Superseded', integrityWarnings: warnings };
    if (report === 'draft') {
      warnings.push('integrity: signed record cannot map back to Draft; customer state is Signed');
    }
    return { state: 'Signed', integrityWarnings: warnings };
  }
  if (report === 'blocked' || signoff === 'pending') {
    return { state: 'Awaiting sign-off', integrityWarnings: warnings };
  }
  if (report === 'draft') return { state: 'Draft', integrityWarnings: warnings };
  return { state: null, integrityWarnings: warnings };
}

export function resolveControlStage(input: WorkspaceStateInput): {
  state: ControlStage | null;
  integrityWarnings: string[];
} {
  if (input.customerControlStage) {
    return { state: input.customerControlStage as ControlStage, integrityWarnings: [] };
  }
  const warnings: string[] = [];
  if (input.caseExists === false) return { state: null, integrityWarnings: warnings };

  const haystack = [
    input.workflowStatusLabel,
    input.workflowStatus,
    input.correctiveActionStatusLabel,
    input.correctiveActionStatus,
    input.safetyCaseStatusLabel,
    input.safetyCaseStatus,
  ].map(normalize).filter(Boolean).join(' ');

  if (!haystack) return { state: 'Confirmed', integrityWarnings: warnings };

  for (const stage of CONTROL_STAGES) {
    if (haystack === normalize(stage)) return { state: stage, integrityWarnings: warnings };
  }

  if (/\breopen/.test(haystack)) return { state: 'Reopened', integrityWarnings: warnings };
  if (/\b(closed|resolved|archived)\b/.test(haystack)) return { state: 'Closed', integrityWarnings: warnings };
  if (/\bmonitor/.test(haystack)) return { state: 'Monitoring', integrityWarnings: warnings };
  if (/effectiveness|impact review|measurement review/.test(haystack)) {
    return { state: 'Effectiveness review required', integrityWarnings: warnings };
  }
  if (/follow-?up measurement scheduled|followup.*schedul|remeasure/.test(haystack)) {
    return { state: 'Follow-up measurement scheduled', integrityWarnings: warnings };
  }
  if (/verification required|field verification|implementation verification/.test(haystack)) {
    return { state: 'Implementation verification required', integrityWarnings: warnings };
  }
  if (/measurement in progress|impact.*(pending|in progress)/.test(haystack)) {
    return { state: 'Measurement in progress', integrityWarnings: warnings };
  }
  if (/implementation in progress|implementing/.test(haystack)) {
    return { state: 'Implementation in progress', integrityWarnings: warnings };
  }
  // Corrective started / in progress ⇒ control follow-up underway (avoid stuck Confirmed).
  if (/\b(started|in_progress)\b|\bin progress\b/.test(haystack)) {
    return { state: 'Monitoring', integrityWarnings: warnings };
  }
  if (/action[_\s-]?assigned|owner_assigned|under_followup|action_pending|\bassign/.test(haystack)) {
    return { state: 'Action assigned', integrityWarnings: warnings };
  }
  if (/control under review|under review|control_review/.test(haystack)) {
    return { state: 'Control under review', integrityWarnings: warnings };
  }
  if (/\bvalidated\b/.test(haystack)) return { state: 'Monitoring', integrityWarnings: warnings };
  if (/\b(confirmed|active|open|action_ready)\b/.test(haystack)) {
    return { state: 'Confirmed', integrityWarnings: warnings };
  }

  warnings.push(`integrity: unmapped control-stage cue '${haystack.slice(0, 80)}'; customer fallback Confirmed`);
  return { state: 'Confirmed', integrityWarnings: warnings };
}

export function resolveWorkspaceCustomerState(input: WorkspaceStateInput): ResolvedWorkspaceState {
  if (input.customerStatusLabel || input.customerScanWorkflowState) {
    const scan = resolveScanWorkflowState(input);
    const record = resolveRecordState(input);
    const control = resolveControlStage(input);
    return {
      scanWorkflowState: scan.state,
      scanResult: scan.state === 'Insufficient coverage'
        ? 'Not measured · insufficient usable coverage'
        : scan.state === 'Failed'
          ? 'Failed'
          : null,
      recordState: record.state,
      controlStage: control.state,
      riskPatternWorkState: input.customerRiskPatternWorkState || null,
      attentionWorkState: input.customerAttentionWorkState || null,
      customerLabel: String(input.customerStatusLabel || scan.state || record.state || '').trim(),
      integrityWarnings: [...(input.integrityWarnings || []), ...scan.integrityWarnings, ...record.integrityWarnings, ...control.integrityWarnings],
    };
  }

  const scan = resolveScanWorkflowState(input);
  const record = resolveRecordState(input);
  const control = resolveControlStage(input);
  const riskWork = (input.isCandidate || normalize(input.reportStatus) === 'candidate')
    ? 'Review required'
    : null;

  let attentionWork: string | null = null;
  if (input.hasNewActivity) attentionWork = 'New activity';
  else if (scan.state === 'Insufficient coverage') attentionWork = 'Insufficient coverage';
  else if (scan.state === 'Failed') attentionWork = 'Failed';
  else if (scan.state === 'Awaiting sign-off') attentionWork = 'Awaiting sign-off';
  else if (riskWork) attentionWork = riskWork;

  const scanResult = scan.state === 'Insufficient coverage'
    ? 'Not measured · insufficient usable coverage'
    : scan.state === 'Failed'
      ? 'Failed'
      : null;

  let customerLabel = riskWork || scan.state || record.state || control.state || '';
  if (input.compoundLabel) {
    customerLabel = collapseCompoundCustomerLabel(input.compoundLabel) || customerLabel;
  }

  return {
    scanWorkflowState: scan.state,
    scanResult,
    recordState: record.state,
    controlStage: control.state,
    riskPatternWorkState: riskWork,
    attentionWorkState: attentionWork,
    customerLabel,
    integrityWarnings: [
      ...scan.integrityWarnings,
      ...record.integrityWarnings,
      ...control.integrityWarnings,
    ],
  };
}

/** Prefer server-resolved Scan workflow state; else derive via shared resolver. */
export function mapScanWorkflowState(input: {
  reportStatus?: string | null;
  signoffStatus?: string | null;
  historyStatus?: string | null;
  impactMeasurementStatus?: string | null;
  recommendedNextStep?: string | null;
  blockerCodes?: string[];
  customerScanWorkflowState?: string | null;
}): ScanWorkflowState {
  const resolved = resolveScanWorkflowState(input);
  // Phase 1 registers only surface a subset; default unknown → Review required.
  return (resolved.state || 'Review required') as ScanWorkflowState;
}

export function mapControlStage(input: {
  workflowStatus?: string | null;
  workflowStatusLabel?: string | null;
  correctiveActionStatus?: string | null;
  correctiveActionStatusLabel?: string | null;
  safetyCaseStatus?: string | null;
  safetyCaseStatusLabel?: string | null;
  customerControlStage?: string | null;
}): ControlStage {
  if (input.customerControlStage) return input.customerControlStage as ControlStage;
  return resolveControlStage(input).state || 'Confirmed';
}

/** Coverage label derived from impact-measurement status and hard coverage blockers. */
export function mapScanCoverageLabel(input: {
  impactMeasurementStatus?: string | null;
  recommendedNextStep?: string | null;
  blockerCodes?: string[];
  workflowState: ScanWorkflowState;
}): string {
  if (input.workflowState === 'Insufficient coverage') return 'Insufficient';

  const step = normalize(input.recommendedNextStep);
  if (step === 'complete_impact_coverage' || step === 'resolve_coverage_issue') return 'Insufficient';

  const hasHardCoverageBlocker = (input.blockerCodes || []).some((code) => {
    const normalized = normalize(code);
    return (
      normalized === 'complete_impact_coverage'
      || normalized === 'insufficient_coverage'
      || normalized === 'resolve_coverage_issue'
      || (normalized.includes('coverage') && !normalized.includes('measurement_pending'))
    );
  });
  if (hasHardCoverageBlocker) return 'Insufficient';

  const impact = normalize(input.impactMeasurementStatus);
  if (impact === 'completed' || impact === 'covered') return 'Sufficient';
  if (impact.includes('insufficient')) return 'Insufficient';

  // Signed scans may retain parent impact_measurement_status=pending; that is not a
  // coverage failure once the scan is signed — show unknown instead of Insufficient.
  if (input.workflowState === 'Signed' && (impact === 'pending' || impact === 'mixed' || !impact)) {
    return '—';
  }

  if (impact === 'pending' || impact === 'mixed') return 'Insufficient';
  return '—';
}

export function mapScanResultLabel(input: { workflowState: ScanWorkflowState }): string {
  if (input.workflowState === 'Insufficient coverage') return 'Not measured · insufficient usable coverage';
  if (input.workflowState === 'Failed') return 'Failed';
  return '—';
}

export function isAwaitingSignoffState(
  scanStatusKind?: string | null,
  customerScanWorkflowState?: string | null,
): boolean {
  if (customerScanWorkflowState) {
    return customerScanWorkflowState === 'Awaiting sign-off';
  }
  const kind = normalize(scanStatusKind);
  return kind === 'draft' || kind === 'awaiting_signoff' || kind === 'awaiting sign-off';
}
